import { Plus, X } from "lucide-react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../ui/components/ui/form";
import { Input } from "../../../ui/components/ui/input";
import { COMPANY_CATEGORIES, CompanyCategory, OnboardingFormData } from "../onboarding-schema";

interface LocationDataStepProps {
  form: UseFormReturn<OnboardingFormData>;
}

const LocationDataStep = ({ form }: LocationDataStepProps) => {
  const locations = form.watch("locations") ?? [];

  return (
    <div>
      <p className="text-sm font-medium mb-1" style={{ color: "#0099ce" }}>
        Schritt 4 von 9
      </p>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">
        Geben Sie die Daten Ihrer Standorte ein.
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Für jeden Standort benötigen wir die Adresse und einen Ansprechpartner,
        damit wir Ihre Kampagnen regional korrekt aufsetzen können.
      </p>

      <div className="space-y-6">
        {locations.map((_, index) => (
          <LocationDataCard key={index} form={form} index={index} />
        ))}
      </div>
    </div>
  );
};

const LocationDataCard = ({
  form,
  index,
}: {
  form: UseFormReturn<OnboardingFormData>;
  index: number;
}) => {
  const categories: CompanyCategory[] = form.watch(`locations.${index}.categories`) ?? [];

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: `locations.${index}.additionalEmails` as any,
  });

  const categoryLabels = categories
    .map((val) => COMPANY_CATEGORIES.find((c) => c.value === val)?.label)
    .filter(Boolean);

  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-8">
      <div className="mb-6">
        <p className="text-base font-semibold text-gray-900 mb-1">
          Standort {index + 1}
        </p>
        {categoryLabels.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {categoryLabels.map((label) => (
              <span
                key={label}
                className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600"
              >
                {label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* Name des Standorts */}
        <FormField
          control={form.control}
          name={`locations.${index}.name`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">
                Name des Standorts <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="z.B. Pflegeheim Zürich"
                  className="mt-1"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Straße & Hausnummer */}
        <FormField
          control={form.control}
          name={`locations.${index}.street`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">
                Straße & Hausnummer <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="z.B. Musterstraße 123"
                  className="mt-1"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* PLZ + Ort */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name={`locations.${index}.postCode`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  PLZ <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="z.B. 8000"
                    className="mt-1"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`locations.${index}.placeName`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Ort <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="z.B. Zürich"
                    className="mt-1"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="border-t border-gray-100 pt-4 mt-4" />

        {/* Ansprechpartner Name */}
        <FormField
          control={form.control}
          name={`locations.${index}.contactName`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">
                Name Ansprechpartner <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Vor- und Nachname"
                  className="mt-1"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Telefon + Email */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name={`locations.${index}.contactPhone`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  Telefon <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="z.B. +41 76 391 69 37"
                    className="mt-1"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`locations.${index}.contactEmail`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  E-Mail <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder="z.B. name@firma.de"
                    className="mt-1"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Weitere Empfängeremailadressen */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">
            Weitere Empfängeremailadressen{" "}
            <span className="text-gray-400 font-normal">(optional)</span>
          </p>

          {fields.length > 0 && (
            <div className="space-y-2 mb-3">
              {fields.map((emailField, emailIndex) => (
                <div key={emailField.id} className="flex items-center gap-2">
                  <FormField
                    control={form.control}
                    name={`locations.${index}.additionalEmails.${emailIndex}`}
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            placeholder="z.B. kollege@firma.de"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => remove(emailIndex)}
                    className="p-2 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => append("" as any)}
            className="w-full rounded-lg border-2 border-dashed border-gray-300 py-2.5 px-4 text-sm font-medium text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Weitere E-Mail hinzufügen
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationDataStep;
